import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
  message
} from "antd";
import { FilePdfOutlined, FileDoneOutlined, RiseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import {
  formatCents,
  formatDate
} from "../../../serviceProvider/components/accounting/shared/money";
import ProgressAdvanceModal from "./ProgressAdvanceModal";
import * as api from "./salesApi";

const CONFIRMABLE = ["Accepted", "Partially Invoiced", "Invoiced"];
const TYPE_LABEL = {
  invoice: "Invoice",
  partial: "Partial invoice",
  advance: "Advance invoice",
  final: "Final invoice",
  credit_note: "Invoice correction",
  cancellation: "Cancellation",
  correction_notice: "Correction notice"
};
const STATUS_LABEL = {
  draft: "Draft",
  sent: "Sent",
  partially_paid: "Partially paid",
  paid: "Paid",
  overdue: "Overdue",
  reminded_1: "Reminded",
  reminded_2: "Reminded",
  reminded_3: "Reminded",
  handed_over: "Handed over",
  settled: "Settled",
  cancelled: "Cancelled",
  written_off: "Written off",
  partially_written_off: "Partially written off"
};

/** Order-confirmation dialog: optional email to the customer (or another address). */
export const ConfirmOfferModal = ({ open, offer, onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [email, setEmail] = useState(false);
  const [to, setTo] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail(false);
      setTo(offer?.client?.email || "");
    }
  }, [open, offer]);
  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.confirmOffer(offer._id, { email, to: email ? to : "" });
      const { offer: saved, emailedTo, emailError } = res.data;
      message.success(`${t("Order confirmation")} ${saved.confirmationNumber}`);
      if (emailedTo) message.success(`${t("Email sent to")} ${emailedTo}`);
      if (emailError) message.warning(`${t("Email not sent")}: ${emailError}`);
      onDone?.(saved);
      onClose();
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={t("Create order confirmation")}
      title={t("Create order confirmation")}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Typography.Text>
          {t(
            "The order confirmation gets its own number and a PDF. It has no effect on the books."
          )}
        </Typography.Text>
        {offer?.confirmationNumber ? (
          <Alert
            type="info"
            showIcon
            message={`${t("Already confirmed")}: ${offer.confirmationNumber}`}
          />
        ) : null}
        <Checkbox checked={email} onChange={e => setEmail(e.target.checked)}>
          {t("Send by email")}
        </Checkbox>
        {email ? (
          <Input
            type="email"
            value={to}
            onChange={e => setTo(e.target.value)}
            placeholder={t("Email address")}
          />
        ) : null}
      </Space>
    </Modal>
  );
};

/**
 * AO-3: what has been invoiced, received and credited for an offer and what is left, with the
 * order confirmation (AO-2) and the Abschlag by progress (ABS-5).
 */
const OfferBillingPanel = ({ offer, onChanged, compact = false }) => {
  const fmtMoney = cents => formatCents(cents, { currency: offer?.currency });
  const { t } = useTranslation("accounting");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);

  const load = useCallback(async () => {
    if (!offer?._id) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.billingSummary(offer._id);
      setData(res.data);
    } catch (err) {
      setError(api.errorText(err));
    } finally {
      setLoading(false);
    }
  }, [offer?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    load();
    onChanged?.();
  };

  const downloadPdf = async () => {
    try {
      await api.openBlob(api.confirmationPdf(offer._id));
    } catch (err) {
      message.error(api.errorText(err));
    }
  };

  if (error)
    return (
      <Alert
        type="warning"
        showIcon
        message={t("Billing overview unavailable")}
        description={error}
      />
    );

  const status = data?.offer?.status || offer?.status;
  const confirmationNumber = data?.offer?.confirmationNumber || offer?.confirmationNumber;
  const kpis = data
    ? [
        { key: "offer", title: t("Offer (net)"), value: data.offerNet },
        { key: "invoiced", title: t("Invoiced (net)"), value: data.invoiced.net },
        { key: "received", title: t("Received"), value: data.received },
        { key: "credited", title: t("Credited (net)"), value: data.credited.net },
        { key: "remaining", title: t("Left to invoice (net)"), value: data.remainingNet }
      ]
    : [];

  const docColumns = [
    {
      title: t("Number"),
      dataIndex: "invoiceNumber",
      mobile: "title",
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <span>{v || (r.draft ? t("Draft") : "—")}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {r.title}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: t("Type"),
      dataIndex: "type",
      render: (v, r) => (
        <Space size={4} wrap>
          <Tag>{t(TYPE_LABEL[v] || v)}</Tag>
          {r.deductedInFinal ? <Tag color="purple">{t("Deducted in final invoice")}</Tag> : null}
        </Space>
      )
    },
    { title: t("Date"), dataIndex: "issueDate", render: v => formatDate(v) },
    {
      title: t("Status"),
      dataIndex: "status",
      mobile: "subtitle",
      render: v => (
        <Tag color={v === "cancelled" ? "red" : v === "paid" ? "green" : "blue"}>
          {t(STATUS_LABEL[v] || v)}
        </Tag>
      )
    },
    { title: t("Net"), dataIndex: "net", align: "end", render: v => fmtMoney(v) },
    { title: t("Open"), dataIndex: "open", align: "end", render: v => fmtMoney(v) }
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        {CONFIRMABLE.includes(status) ? (
          <Button icon={<FileDoneOutlined />} onClick={() => setConfirmOpen(true)}>
            {confirmationNumber ? t("Order confirmation") : t("Create order confirmation")}
          </Button>
        ) : null}
        {confirmationNumber ? (
          <Button icon={<FilePdfOutlined />} onClick={downloadPdf}>
            {confirmationNumber}
          </Button>
        ) : null}
        {!["Draft", "Declined"].includes(status) ? (
          <Button icon={<RiseOutlined />} onClick={() => setProgressOpen(true)}>
            {t("Abschlag by progress")}
          </Button>
        ) : null}
      </Space>
      {data?.offer?.offerNumber || confirmationNumber ? (
        <Descriptions size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 12 }}>
          <Descriptions.Item label={t("Offer number")}>
            {data?.offer?.offerNumber || "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("Order confirmation")}>
            {confirmationNumber
              ? `${confirmationNumber} · ${formatDate(data?.offer?.confirmedAt)}`
              : "—"}
          </Descriptions.Item>
        </Descriptions>
      ) : null}
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        {kpis.map(k => (
          <Col xs={12} sm={8} md={compact ? 8 : 4} key={k.key}>
            <Card size="small" loading={loading}>
              <Statistic
                title={k.title}
                value={fmtMoney(k.value)}
                valueStyle={{
                  fontSize: 16,
                  color:
                    k.key === "remaining" && k.value < 0 ? "var(--ds-danger, #cf1322)" : undefined
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>
      {data && !compact ? (
        <>
          <Typography.Text type="secondary">
            {t("Cancelled")}: {data.cancelled.count} · {t("Drafts")}: {data.drafts.count} (
            {fmtMoney(data.drafts.net)}) · {t("Open")}: {fmtMoney(data.open)}
            {data.retention ? ` · ${t("Retention")}: ${fmtMoney(data.retention)}` : ""}
          </Typography.Text>
          <ResponsiveTable
            style={{ marginTop: 12 }}
            rowKey="_id"
            size="small"
            pagination={false}
            dataSource={data.documents || []}
            columns={docColumns}
          />
        </>
      ) : null}
      <ConfirmOfferModal
        open={confirmOpen}
        offer={{ ...offer, confirmationNumber }}
        onClose={() => setConfirmOpen(false)}
        onDone={changed}
      />
      <ProgressAdvanceModal
        open={progressOpen}
        offer={{ ...offer, offerNumber: data?.offer?.offerNumber || offer?.offerNumber }}
        onClose={() => setProgressOpen(false)}
        onCreated={changed}
      />
    </div>
  );
};

export default OfferBillingPanel;
